import { Flex, Button } from '@chakra-ui/react';
import React from 'react';
import { Navigate, useParams, Link as RouterLink } from 'react-router-dom';
import { z } from 'zod';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { FundingRequestEntitiesFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { useEditLoanContext } from '../../useEditLoanContext';
import { FundingRequestEditForm } from './FundingRequestEditForm';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { dashboardRoutePadding } from '~/packages/common-frontend/constants';
import { truncateUuid } from '~/packages/common-frontend/utils';

// Types
const RouteParamsStruct = z.object({
  loanId: z.string({ message: 'Missing URL parameter' }),
  fundingRequestId: z.string({ message: 'Missing URL parameter' }),
});

const RouteBase = () => {
  const params = useParams();
  const { loanId, fundingRequestId } = RouteParamsStruct.parse(params);

  const context = useEditLoanContext();

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      gap="2rem"
      padding={dashboardRoutePadding}
    >
      <Flex w="100%" justify="space-between" alignItems="center">
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Funding', path: '/' },
            { label: 'Loans', path: '/loans' },
            context.status === 'ready'
              ? {
                  label: `Edit ${context.state.originalResolvedLoan.name}`,
                  path: `/loans/${loanId}/edit`,
                }
              : {
                  type: 'loading',
                },
          ]}
        />
      </Flex>
      <Flex width="100%" justify="space-between" alignItems="center">
        <Flex gap="1rem" alignItems="center" justify="space-between" w="100%">
          {(() => {
            if (context.status === 'ready') {
              const { originalResolvedLoan, newEntities } = context.state;

              let fundingRequest:
                | FundingRequestEntitiesFragmentGQLType
                | undefined;
              if (newEntities) {
                fundingRequest = newEntities.newResolvedLoan.funding.requests.find(
                  (request) => request.fundingRequestId === fundingRequestId,
                );
              } else {
                fundingRequest = originalResolvedLoan.funding.requests.find(
                  (request) => request.fundingRequestId === fundingRequestId,
                );
              }
              finleyAssert(
                fundingRequest,
                `Failed to find funding request [id:${fundingRequestId}]`,
              );

              return (
                <Typography variant="page-title" trim>
                  Edit{' '}
                  {
                    context.state.originalResolvedLoan.facility
                      .creditFacilityName
                  }{' '}
                  {commonUtils.toTitleCase(fundingRequest.fundingRequestType)}{' '}
                  Request
                </Typography>
              );
            }
            return (
              <Typography variant="page-title" trim>
                Edit Funding Request
              </Typography>
            );
          })()}

          <Flex alignItems="center" gap="1rem">
            <Flex
              borderRadius="2rem"
              borderColor="gray.200"
              borderWidth="1px"
              padding="1rem"
              alignItems="center"
            >
              <Typography fontWeight="bold" TextProps={{ mr: '0.5rem' }}>
                Request
              </Typography>
              <Typography>{truncateUuid(fundingRequestId)}</Typography>
            </Flex>

            <Button
              variant="primary"
              colorScheme="v2-aurora"
              as={RouterLink}
              to={`/loans/${loanId}/edit`}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      </Flex>
      <FundingRequestEditForm fundingRequestId={fundingRequestId} />
    </Flex>
  );
};

export const Route = () => {
  const params = useParams();
  const { loanId } = RouteParamsStruct.parse(params);

  return (
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to={`/loans/${loanId}/edit`} />;
      }}
    >
      <RouteBase />
    </RouteErrorBoundary>
  );
};
